import React, { useCallback, useMemo, useState, type RefCallback } from 'react';
import type { TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useGetFormatRulesClick } from '@atlassian/jira-business-conditional-formatting-sidebar/src/ui/format-rules-sidebar/format-button-simple/utils.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ThemedIconButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import { moreMenuContentEntryPoint } from './more-menu-content/entrypoint.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	fallbackMenu: {
		width: '115px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
});

export const ListMoreMenu = () => {
	const { formatMessage } = useIntl();

	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
	const closeMoreMenu = useCallback(() => setIsMoreMenuOpen(false), []);
	const toggleMoreMenu = useCallback(() => setIsMoreMenuOpen((oldIsOpen) => !oldIsOpen), []);

	const { onClickFormatButton } = useGetFormatRulesClick();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		moreMenuContentEntryPoint,
		{},
	);
	const moreMenuContentTrigger = useEntryPointButtonTrigger(entryPointActions, !isMoreMenuOpen);
	const moreMenuProps = useMemo(
		() => ({
			onClose: closeMoreMenu,
			onClickFormatButton,
		}),
		[closeMoreMenu, onClickFormatButton],
	);

	const moreMenuContent = useCallback(
		() => (
			<Box>
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="business-list-more-menu"
					packageName="@atlassian/jira-business-list"
					runtimeProps={moreMenuProps}
					fallback={
						<Box xcss={styles.fallbackMenu}>
							<Spinner />
						</Box>
					}
				/>
			</Box>
		),
		[entryPointReferenceSubject, moreMenuProps],
	);

	const moreMenuTrigger = useCallback(
		(triggerProps: TriggerProps | { ref: RefCallback<HTMLElement> }) => (
			<Tooltip content={formatMessage(messages.triggerLabel)} position="top" hideTooltipOnClick>
				{isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons') ? (
					<ThemedIconButton
						{...triggerProps}
						appearance="default"
						icon={ShowMoreHorizontalIcon}
						ref={(element: HTMLButtonElement) => {
							moreMenuContentTrigger(element);
							if (typeof triggerProps.ref === 'function') {
								triggerProps.ref(element);
							}
						}}
						isSelected={isMoreMenuOpen}
						onClick={toggleMoreMenu}
						label={formatMessage(messages.triggerLabel)}
						testId="business-list.ui.list-view.header-actions.more-menu.more-menu"
					/>
				) : (
					<ThemedButton
						{...triggerProps}
						ref={(element: HTMLElement) => {
							moreMenuContentTrigger(element);
							if (typeof triggerProps.ref === 'function') {
								triggerProps.ref(element);
							}
						}}
						iconAfter={<ShowMoreHorizontalIcon label="" spacing="spacious" color="currentColor" />}
						isSelected={isMoreMenuOpen}
						onClick={toggleMoreMenu}
						aria-label={formatMessage(messages.triggerLabel)}
						testId="business-list.ui.list-view.header-actions.more-menu.more-menu-old"
					/>
				)}
			</Tooltip>
		),
		[formatMessage, isMoreMenuOpen, moreMenuContentTrigger, toggleMoreMenu],
	);

	return (
		<Popup
			isOpen={isMoreMenuOpen}
			onClose={closeMoreMenu}
			placement="bottom-end"
			content={moreMenuContent}
			trigger={moreMenuTrigger}
			testId="business-list.ui.list-view.header-actions.more-menu.more-menu-popup"
			messageId="business-list.ui.list-view.header-actions.more-menu.popup.more-menu-popup"
			messageType="transactional"
		/>
	);
};
